import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { Compass } from 'lucide-react';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';
import OfficeModalHeader from './office/OfficeModalHeader';
import { cn } from '../lib/utils';

const paletteInputWrap =
  'mx-3 mt-3 rounded-lg border border-border bg-muted/40 px-2';

const paletteFooter =
  'border-t border-border/80 px-4 pt-3 text-[10px] text-muted-foreground [&_kbd]:rounded [&_kbd]:border [&_kbd]:border-border [&_kbd]:bg-muted/60 [&_kbd]:px-1.5 [&_kbd]:py-0.5 [&_kbd]:font-mono [&_kbd]:text-muted-foreground';

const itemSelected =
  'aria-selected:bg-muted aria-selected:text-foreground aria-selected:ring-1 aria-selected:ring-border';

/**
 * Paleta Cmd/Ctrl+K: lista todas as rotas dos módulos visíveis (filtrados por permissão).
 */
export function NavCommandPalette({ open, onOpenChange, modules, badges }) {
  const navigate = useNavigate();

  const handleSelect = (path) => {
    navigate(path);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn('max-w-xl gap-0 overflow-hidden p-0 pb-4 sm:rounded-2xl')}
        aria-describedby={undefined}
      >
        <OfficeModalHeader
          layout="stack"
          variant="circle"
          icon={Compass}
          title="Ir para página"
          description="Busque e abra qualquer módulo do painel"
          className="office-modal-header px-4 sm:px-6"
        />
        <Command className="rounded-none bg-transparent text-foreground">
          <div className={paletteInputWrap}>
            <Command.Input
              placeholder="Buscar página…"
              className={cn(
                'flex h-10 w-full border-0 bg-transparent px-2 text-sm text-foreground outline-none',
                'placeholder:text-muted-foreground',
              )}
            />
          </div>
          <Command.List className="max-h-[min(55vh,420px)] overflow-y-auto px-2 py-2">
            <Command.Empty className="py-8 text-center text-sm text-muted-foreground">
              Nenhum resultado.
            </Command.Empty>
            {(modules || []).map((mod) => (
              <Command.Group
                key={mod.id}
                value={mod.id}
                heading={mod.label}
                className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
              >
                {mod.items.map((it) => {
                  const Icon = it.icon;
                  const badge = it.badgeKey ? badges[it.badgeKey] : 0;
                  const searchValue = `${mod.label} ${it.label} ${it.path}`;
                  return (
                    <Command.Item
                      key={it.path}
                      value={searchValue}
                      onSelect={() => handleSelect(it.path)}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-foreground',
                        '[&[aria-selected=true]_svg]:text-primary [&[data-selected=true]_svg]:text-primary',
                        itemSelected,
                      )}
                    >
                      <Icon size={16} strokeWidth={1.75} className="shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1 truncate font-medium">{it.label}</span>
                      {badge > 0 && (
                        <span className="inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                          {badge > 99 ? '99+' : badge}
                        </span>
                      )}
                      <span className="hidden shrink-0 text-[10px] text-muted-foreground sm:inline">
                        {it.path}
                      </span>
                    </Command.Item>
                  );
                })}
              </Command.Group>
            ))}
          </Command.List>
          <div className={paletteFooter}>
            <kbd>Esc</kbd> fechar · <kbd>↑</kbd>
            <kbd>↓</kbd> navegar · <kbd>↵</kbd> abrir
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

export default NavCommandPalette;
